import localhost from "apis/localhost";
import {
  displayNoFilterResults,
  displayNoSearchResults,
} from "components/Modal/confModalTypes";
import { FILTER_TYPES } from "constants/discovery";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  clearJobQueue,
  removeFromJobQueue,
} from "slices/app/appSlice";
import { applySnapshot } from "slices/discovery/actions/snapshot";
import {
  finalKeys,
  prepareThemesOriginalCase,
} from "slices/discovery/actions/theme";
import {
  addIgnoredDocument,
  addSearchNounPhrasesToState,
  clearFilterState,
  clearSearchState,
  filterData,
  removeNounPhrases,
  setDisabledDocument,
  setDisableSaveSnapshot,
  setFilterDocID,
  setFilterType,
  setFilterView,
  setSearch,
  setSearchTerm,
  showAllThemesAndItems,
} from "slices/discovery/discoverySlice";
import { getSelectedDocumentIds } from "slices/discovery/helpers/theme";
import { renameKeys } from "utils";

const TERMS_URL = "/discovery/terms";
const ALL_THEMES = "All Themes";

export const cancelGetItems = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { discovery } = getState();
  const groupNames = discovery.themes
    .filter(
      (theme) =>
        theme.GroupName &&
        theme.NounPhrase &&
        theme.GroupName !== ALL_THEMES &&
        theme.checked
    )
    .map((theme) => theme.GroupName);
  const unique = groupNames.filter((v, i) => groupNames.indexOf(v) === i);
  const params = {
    docSetId: discovery.config.folderId,
    documentIds: getSelectedDocumentIds(discovery),
    groupNames: unique,
  };

  await localhost
    .post(TERMS_URL, params)
    .then((response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      dispatch(
        addSearchNounPhrasesToState(prepareThemesOriginalCase(data, groupNames))
      );
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const clearSearchAndFilterState =
  (persistCheckedItems = true, keepAllNPs = false) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));

    const state = getState().discovery;
    const checkedItems = state.themes.filter(
      (theme) =>
        theme.GroupName &&
        theme.NounPhrase &&
        theme.GroupName !== ALL_THEMES &&
        theme.checked
    );

    dispatch(clearSearchState(state));
    dispatch(clearFilterState(state));
    dispatch(showAllThemesAndItems(true));

    if (persistCheckedItems && checkedItems.length) {
      if (!keepAllNPs) {
        await dispatch(cancelGetItems());
      }
    } else {
      dispatch(removeNounPhrases(state));
    }

    dispatch(removeFromJobQueue("genericJob"));
  };

export const applyFilter =
  (filterType, documentId, newSearchTerm, docToIgnoreId, snapshot) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("applyFilter"));

    const {
      themes,
      config: { searchTerm: prevSearchTerm, folderId, filterDocID },
    } = getState().discovery;
    const searchTerm = newSearchTerm ? newSearchTerm : prevSearchTerm;

    let documentIds;
    if (filterType === FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC) {
      documentIds = [filterDocID ?? documentId];
    } else if (snapshot) {
      documentIds = snapshot.checkedDocuments;
    } else {
      documentIds = getSelectedDocumentIds(getState().discovery, filterType);
    }

    if (docToIgnoreId !== null) {
      documentIds = documentIds.filter((docId) => docId !== docToIgnoreId);
    }

    const groupNames = themes
      .filter(
        (theme) =>
          theme.GroupName && !theme.NounPhrase && theme.GroupName !== ALL_THEMES
      )
      .filter((theme) =>
        theme.Documents.some((document) => documentIds.includes(document.Id))
      )
      .map((theme) => theme.GroupName);

    const params = {
      docSetId: folderId,
      documentIds,
      groupNames,
      discoveryFilter: filterType !== null ? filterType : undefined,
      searchTerm: searchTerm !== "" ? searchTerm : undefined,
    };

    await localhost
      .post(TERMS_URL, params)
      .then(async (response) => {
        let { data } = response;
        if (data.length) {
          // clear state
          await dispatch(
            clearSearchAndFilterState(
              (prevSearchTerm || newSearchTerm) && !snapshot ? true : false,
              true
            )
          );
          dispatch(addToJobQueue("applyFilter"));
          dispatch(setSearch(false));

          // apply new data
          data = renameKeys(data, finalKeys);
          const newThemes = prepareThemesOriginalCase(data, groupNames);
          dispatch(filterData(newThemes));
          dispatch(setFilterView(true));
          dispatch(setFilterType(filterType));
          dispatch(setDisableSaveSnapshot(false));
          dispatch(setSearchTerm(searchTerm));
          if (filterType === FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC) {
            dispatch(setDisabledDocument(filterDocID ?? documentId));
            dispatch(setFilterDocID(filterDocID ?? documentId));
          }
          if (docToIgnoreId !== null) {
            dispatch(addIgnoredDocument(docToIgnoreId));
          }
          if (snapshot) {
            dispatch(applySnapshot(snapshot));
          }
        } else {
          if (newSearchTerm) {
            dispatch(displayNoSearchResults(newSearchTerm));
          } else {
            dispatch(displayNoFilterResults(filterType));
          }
        }
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(removeFromJobQueue("applyFilter"));
      });
  };
